import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Target, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/context/UserContext';
import { API } from '@/utils/constants';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithCredentials } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    setLoggingIn(true);
    try {
      await loginWithCredentials(email, password);
      toast.success("Logged in successfully!");
      navigate('/');
    } catch (e) {
      const errorDetail = e.response?.data?.detail;
      if (errorDetail === 'NO_PASSWORD') {
        toast.error("This account has no password set. Please use 'Forgot Password' to set one.");
      } else {
        toast.error(errorDetail || "Invalid email or password");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setResetSending(true);
    try {
      await axios.post(`${API}/forgot-password`, { email: resetEmail });
      toast.success("If an account exists for that email, a reset link has been sent.");
      setResetEmailSent(true);
    } catch (e) {
      // Backend always returns 200 for security; only network errors land here
      toast.error("Could not send reset email. Please try again later.");
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col" data-testid="page-login">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[400px] h-[400px] rounded-full blur-[100px] opacity-20 bg-purple-600 -top-32 -right-32" />
        <div className="absolute w-[300px] h-[300px] rounded-full blur-[80px] opacity-15 bg-blue-600 -bottom-24 -left-24" />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between p-4 pt-6">
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors"
          data-testid="login-back-home-btn"
          aria-label="Back to home"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">MyGoal</span>
        </div>
        <button
          onClick={() => navigate('/')}
          className="text-white/70 hover:text-white text-sm px-3 py-1.5 rounded-full hover:bg-white/5 transition-colors"
          data-testid="login-back-home-text-btn"
        >
          Home
        </button>
      </header>

      {/* Content */}
      <div className="relative z-10 flex-1 p-4">
        <div className="max-w-md mx-auto">
          {!showForgotPassword ? (
            <>
              <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
              <p className="text-white/60 mb-8">Login to continue your journey</p>

              {/* Email/Password Login Form */}
              <form onSubmit={handleCredentialLogin} className="space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-500"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-500"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                {/* Forgot Password Link */}
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
                    className="text-purple-400 text-sm hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loggingIn}
                  className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl disabled:opacity-50 hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                  data-testid="login-submit-button"
                >
                  {loggingIn ? 'Logging in...' : 'Login'}
                </button>
              </form>

              {/* Register link */}
              <div className="text-center mt-8">
                <p className="text-white/50">
                  Don't have an account?{' '}
                  <button
                    onClick={() => navigate('/register')}
                    className="text-purple-400 font-medium hover:underline"
                    data-testid="register-link"
                  >
                    Create one
                  </button>
                </p>
              </div>
            </>
          ) : (
            /* Forgot Password / Reset Flow */
            <>
              <button
                onClick={() => { setShowForgotPassword(false); setShowResetForm(false); }}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </button>

              <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
              <p className="text-white/60 mb-8">
                {resetEmailSent
                  ? "Check your email for the reset link"
                  : "Enter your email and we'll send you a reset link"}
              </p>

              {!resetEmailSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-white/60 text-sm font-medium mb-2">Email Address</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-500"
                      data-testid="reset-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resetSending}
                    className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl disabled:opacity-50"
                    data-testid="request-reset-button"
                  >
                    {resetSending ? 'Sending...' : 'Send reset link'}
                  </button>
                </form>
              ) : (
                <div className="space-y-4" data-testid="reset-email-sent-state">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                    <p className="text-green-400 text-sm">
                      ✓ If an account exists for <strong>{resetEmail}</strong>, an email with a reset link has been sent.
                    </p>
                    <p className="text-white/50 text-xs mt-2">
                      The link expires in 1 hour. Check your spam folder if you don&apos;t see it within a few minutes.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetEmailSent(false);
                    }}
                    className="w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-sm"
                    data-testid="reset-email-sent-back-button"
                  >
                    Back to login
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
